import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Plus,
  Trash2,
  GripVertical,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { resolveNewArchiveIds } from "../utils/archiveMessageIds";

export default function ArchiveManagerModal({
  isOpen,
  onClose,
  torrentId,
  torrentTitle,
}) {
  const [messageIds, setMessageIds] = useState([]);
  const [newId, setNewId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !torrentId) return;
    setLoading(true);
    setError(null);
    fetch(`/api/admin/torrents/${torrentId}/archives`, {
      headers: {
        Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
      },
    })
      .then((r) => r.json())
      .then((data) => {
        setMessageIds(data.archive_message_ids || []);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load archives");
        setLoading(false);
      });
  }, [isOpen, torrentId]);

  const handleAdd = () => {
    const { error: parseError, ids } = resolveNewArchiveIds(newId, messageIds);
    if (ids.length === 0) {
      if (parseError) setError(parseError);
      return;
    }
    setMessageIds((prev) => [...prev, ...ids]);
    setNewId("");
    setError(null);
    inputRef.current?.focus();
  };

  const handleRemove = (index) => {
    setMessageIds((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    setMessageIds((prev) => {
      const arr = [...prev];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      return arr;
    });
  };

  const handleMoveDown = (index) => {
    if (index >= messageIds.length - 1) return;
    setMessageIds((prev) => {
      const arr = [...prev];
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      return arr;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/torrents/${torrentId}/archives`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
        },
        body: JSON.stringify({ archive_message_ids: messageIds }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Save failed");
      }
      onClose(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#1a1a2e] border border-gray-700 rounded-2xl w-full max-w-md mx-4 max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-white">Manage Archives</h2>
            <p className="text-xs text-gray-400 mt-1 truncate max-w-[280px]">
              {torrentTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-700 transition-colors text-gray-400 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center text-gray-400 py-8">Loading...</div>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-3">
                Add Telegram message IDs from the archive channel. Each ID
                represents one file part. Use{" "}
                <span className="font-mono text-gray-400">120:5</span> to add 5
                sequential IDs starting from 120.
              </p>

              {/* List of message IDs */}
              {messageIds.length === 0 ? (
                <div className="text-center text-gray-500 py-6 border border-dashed border-gray-700 rounded-xl mb-4">
                  No archive files linked yet
                </div>
              ) : (
                <div className="space-y-2 mb-4">
                  {messageIds.map((id, index) => (
                    <div
                      key={`${id}-${index}`}
                      className="flex items-center gap-2 bg-gray-800/50 rounded-lg px-3 py-2 border border-gray-700/50"
                    >
                      <GripVertical
                        size={14}
                        className="text-gray-600 flex-shrink-0"
                      />
                      <span className="text-sm text-gray-300 flex-1 font-mono">
                        Part {index + 1} — msg_id: {id}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                          className="p-1 rounded hover:bg-gray-700 text-gray-500 hover:text-white transition-colors disabled:opacity-30"
                        >
                          <ArrowUp size={14} />
                        </button>
                        <button
                          onClick={() => handleMoveDown(index)}
                          disabled={index === messageIds.length - 1}
                          className="p-1 rounded hover:bg-gray-700 text-gray-500 hover:text-white transition-colors disabled:opacity-30"
                        >
                          <ArrowDown size={14} />
                        </button>
                        <button
                          onClick={() => handleRemove(index)}
                          className="p-1 rounded hover:bg-red-900/40 text-gray-500 hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new ID */}
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="text"
                  value={newId}
                  onChange={(e) => setNewId(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="120 or 120:5"
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                />
                <button
                  onClick={handleAdd}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center gap-1 text-sm font-medium"
                >
                  <Plus size={16} />
                  Add
                </button>
              </div>

              {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-700 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-800 transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="flex-1 py-2.5 rounded-lg bg-orange-600 hover:bg-orange-500 text-white transition-colors text-sm font-medium disabled:opacity-50"
          >
            {saving ? "Saving..." : `Save (${messageIds.length} parts)`}
          </button>
        </div>
      </div>
    </div>
  );
}
